import { c as _c } from "react/compiler-runtime";
import DropsList from "@/components/drops/view/DropsList";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
import { WaveDropsScrollControls } from "@/components/waves/drops/WaveDropsScrollControls";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject, Ref } from "react";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsMessageListSectionProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly scrollContainerCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly unreadDividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
  readonly onScrollToUnread?: ((serialNo: number) => void) | undefined;
  readonly onDismissUnread: () => void;
  readonly autoCollapseSerials?: ReadonlySet<number> | undefined;
  readonly suspendLightDropHydration?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
const MIN_DROPS_FOR_PAGINATION = 25;
export const WaveDropsMessageListSection: React.FC<WaveDropsMessageListSectionProps> = t0 => {
  const $ = _c(45);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    onTopIntersection,
    onReply,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    unreadDividerSerialNo,
    unreadCount,
    boostedDrops,
    onBoostedDropClick,
    onScrollToUnread,
    onDismissUnread,
    autoCollapseSerials,
    suspendLightDropHydration: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const suspendLightDropHydration = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const hasNextPage = !!waveMessages?.hasNextPage && waveMessages.drops.length >= MIN_DROPS_FOR_PAGINATION;
  const containerRef = scrollContainerCallbackRef ?? scrollContainerRef;
  const anchorRef = bottomAnchorCallbackRef ?? bottomAnchorRef;
  const t4 = !!waveMessages?.isLoadingNextPage;
  let t5;
  if ($[0] !== waveMessages?.drops) {
    t5 = waveMessages?.drops ?? [];
    $[0] = waveMessages?.drops;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== activeDrop || $[3] !== autoCollapseSerials || $[4] !== boostedDrops || $[5] !== dropId || $[6] !== isVotingClosed || $[7] !== isVotingControlsLocked || $[8] !== onBoostedDropClick || $[9] !== onDropContentClick || $[10] !== onQuoteClick || $[11] !== onReply || $[12] !== queueSerialTarget || $[13] !== scrollContainerRef || $[14] !== serialTarget || $[15] !== suspendLightDropHydration || $[16] !== t5 || $[17] !== targetDropRef || $[18] !== unreadDividerSerialNo || $[19] !== winningThreshold) {
    t6 = <DropsList scrollContainerRef={scrollContainerRef} onReplyClick={queueSerialTarget} drops={t5} showWaveInfo={false} onReply={onReply} showReplyAndQuote={true} activeDrop={activeDrop} serialNo={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} parentContainerRef={scrollContainerRef} dropViewDropId={dropId} onDropContentClick={onDropContentClick} unreadDividerSerialNo={unreadDividerSerialNo} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={suspendLightDropHydration} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} key="drops-list" />;
    $[2] = activeDrop;
    $[3] = autoCollapseSerials;
    $[4] = boostedDrops;
    $[5] = dropId;
    $[6] = isVotingClosed;
    $[7] = isVotingControlsLocked;
    $[8] = onBoostedDropClick;
    $[9] = onDropContentClick;
    $[10] = onQuoteClick;
    $[11] = onReply;
    $[12] = queueSerialTarget;
    $[13] = scrollContainerRef;
    $[14] = serialTarget;
    $[15] = suspendLightDropHydration;
    $[16] = t5;
    $[17] = targetDropRef;
    $[18] = unreadDividerSerialNo;
    $[19] = winningThreshold;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      height: "1px"
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== anchorRef) {
    t8 = <div ref={anchorRef} style={t7} />;
    $[22] = anchorRef;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== bottomPaddingClassName || $[25] !== containerRef || $[26] !== hasNextPage || $[27] !== onTopIntersection || $[28] !== t4 || $[29] !== t6 || $[30] !== t8) {
    t9 = <WaveDropsReverseContainer ref={containerRef} isFetchingNextPage={t4} hasNextPage={hasNextPage} onTopIntersection={onTopIntersection} bottomPaddingClassName={bottomPaddingClassName}>{t6}{t8}</WaveDropsReverseContainer>;
    $[24] = bottomPaddingClassName;
    $[25] = containerRef;
    $[26] = hasNextPage;
    $[27] = onTopIntersection;
    $[28] = t4;
    $[29] = t6;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== isAtBottom || $[33] !== onDismissUnread || $[34] !== onRevealPending || $[35] !== onScrollToUnread || $[36] !== pendingCount || $[37] !== scrollContainerRef || $[38] !== scrollToBottom || $[39] !== unreadCount || $[40] !== unreadDividerSerialNo) {
    t10 = onScrollToUnread && <WaveDropsScrollControls unreadDividerSerialNo={unreadDividerSerialNo ?? null} unreadCount={unreadCount} scrollContainerRef={scrollContainerRef} onScrollToUnread={onScrollToUnread} onDismissUnread={onDismissUnread} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} newMessagesCount={pendingCount} onRevealNewMessages={onRevealPending} />;
    $[32] = isAtBottom;
    $[33] = onDismissUnread;
    $[34] = onRevealPending;
    $[35] = onScrollToUnread;
    $[36] = pendingCount;
    $[37] = scrollContainerRef;
    $[38] = scrollToBottom;
    $[39] = unreadCount;
    $[40] = unreadDividerSerialNo;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
};